import { Button, Input, Space } from "antd";
import { ColumnsType } from "antd/es/table";
import { useCallback, useEffect } from "react";
import AddIcon from "../../../assets/icons/AddIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import SearchIcon from "../../../assets/icons/SearchIcon";
import ViewIcon from "../../../assets/icons/ViewIcon";
import ManageContentCategory from "../../../components/admin/ManageContent/ManageContentCategory";
import FilterSection from "../../../components/common/FilterSection";
import ActionButton from "../../../components/ui/ActionButton";
import CustomTable from "../../../components/ui/CustomTable";
import useDebounce from "../../../hooks/useDebounce";
import useModal from "../../../hooks/useModal";
import useTable from "../../../hooks/useTable";
import AdminLayout from "../../../layouts/AdminLayout";
import { formatDate, formatTime } from "../../../lib/time";
import { getAllCategoryAsync } from "../../../store/features/category/categoryApi";
import { useAppDispatch, useAppSelector } from "../../../store/store";
import { TableData } from "../../../types";
import { GetCategoryData } from "../../../types/category/category";
import { countRow } from "../../../utils/number";
import { addKeyInArray, changeStatus, skipData } from "../../../utils/utils";

type Status = "Published" | "Draft" | "Archived";

const dealerStatusClass = {
  Published: "status-done",
  Draft: "status-draft",
  Archived: "status-pending",
};

export default function ContentCategory() {
  const dispatch = useAppDispatch();
  const { keyword, handleInputChange } = useDebounce();
  const { open, toggle, handleOpen } = useModal(reRender);
  const { limit, page, handlePagination, handleLimit, resetPagination } =
    useTable();
  const { CategoryList, countCategory, loading } = useAppSelector(
    (state) => state.category
  );

  const columns: ColumnsType<TableData<GetCategoryData>> = [
    {
      title: "#SL",
      align: "center",
      width: 60,
      render: (_, __, idx) => countRow(page, limit, idx),
    },
    {
      title: "Category",
      dataIndex: "categoryName",
      render: (val) => <span>{val || "N/A"}</span>,
    },
    {
      title: "Category Type",
      dataIndex: "categoryType",
      render: (val) => <span>{val || "N/A"}</span>,
    },
    {
      title: "Date",
      dataIndex: "updatedAt",
      render: (val) => (
        <div className="">
          <p>{formatDate(val)}</p>
          <p>{formatTime(val)}</p>
        </div>
      ),
    },
    {
      title: "Status",
      align: "center",
      dataIndex: "categoryStatus",
      render: (val: Status) => (
        <span className={dealerStatusClass[val]}>{changeStatus(val)}</span>
      ),
    },

    {
      title: "Action",
      align: "center",
      width: 100,
      render: (val) => (
        <Space size="small">
          <ActionButton
            icon={<ViewIcon />}
            onClick={() =>
              handleOpen({
                type: "VIEW",
                data: val,
              })
            }
          />
          <ActionButton
            icon={<EditIcon />}
            onClick={() =>
              handleOpen({
                type: "EDIT",
                data: val,
              })
            }
          />
        </Space>
      ),
    },
  ];

  // dispatch content
  const getData = useCallback(() => {
    dispatch(
      getAllCategoryAsync({
        params: {
          skip: skipData(page, limit),
          limit,
          custom_text: keyword,
          categoryType: "Publication",
        },
      })
    );
  }, [dispatch, keyword, limit, page]);

  useEffect(() => {
    getData();
  }, [getData]);

  function reRender() {
    getData();
  }

  return (
    <AdminLayout
      title="Category List"
      className="dashboard"
      headerChildren={
        <Button
          className="primary_btn"
          type="primary"
          size="large"
          icon={<AddIcon />}
          onClick={() =>
            handleOpen({
              type: "ADD",
            })
          }
        >
          Create New Category
        </Button>
      }
    >
      <CustomTable<TableData<GetCategoryData>>
        loading={loading}
        total={countCategory}
        limit={limit}
        page={page}
        handlePagination={handlePagination}
        handleLimit={handleLimit}
        dataList={addKeyInArray(CategoryList)}
        columns={columns}
        bordered={false}
        isLimitRow
        showTotalPage
        tableTopChildren={
          <FilterSection>
            <Input
              size="large"
              suffix={<SearchIcon />}
              placeholder={`Search within ${countCategory} data`}
              onChange={(e) => {
                handleInputChange(e);
                resetPagination();
              }}
            />
          </FilterSection>
        }
      />

      {/* Product create and update form modal */}
      {(open?.type === "ADD" ||
        open?.type === "EDIT" ||
        open?.type === "VIEW") && (
        <ManageContentCategory open={open} handleClose={toggle} />
      )}
    </AdminLayout>
  );
}
